import { useState, useCallback, useRef, useEffect } from "react";
import { useTheme } from "../theme/use-theme";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import {
  GoogleMap,
  useJsApiLoader,
  Libraries,
  StandaloneSearchBox,
} from "@react-google-maps/api";

declare global {
  interface Window {
    google: typeof google;
  }
}

const center = {
  lat: 40.7128,
  lng: -74.006,
};

interface GeoJSONPolygon {
  type: string;
  coordinates: number[][][];
}

interface CampusBoundaryData {
  geometry: GeoJSONPolygon;
}

// DrawingManager was removed from the Maps JS API in v3.65, so we draw the
// polygon manually with core map click listeners (no "drawing" library).
const libraries: Libraries = ["places"];

interface CampusBoundaryProps {
  initialBoundaryData?: CampusBoundaryData | null;
  onBoundaryChange?: (boundary: CampusBoundaryData | null) => void;
  onValidityChange?: (isValid: boolean) => void;
  readOnly?: boolean;
}

const CampusBoundary = ({
  initialBoundaryData = null,
  onBoundaryChange,
  onValidityChange,
  readOnly = false,
}: CampusBoundaryProps) => {
  const { theme } = useTheme();
  const isDark = theme === "dark";

  // Dynamic map container style based on readOnly prop
  const mapContainerStyle = {
    width: "100%",
    height: readOnly ? "800px" : "750px",
    borderRadius: "8px",
    overflow: "hidden",
  };

  const polygonStyle = {
    fillColor: isDark ? "#1e90ff" : "#3388ff",
    fillOpacity: 0.2,
    strokeWeight: 2,
    strokeColor: isDark ? "#1e90ff" : "#3388ff",
  };

  const [boundaryData, setBoundaryData] = useState<CampusBoundaryData | null>(
    initialBoundaryData
  );
  const [hasBeenCleared, setHasBeenCleared] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const [vertexCount, setVertexCount] = useState(0);

  const polygonRef = useRef<google.maps.Polygon | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const searchBoxRef = useRef<google.maps.places.SearchBox | null>(null);

  // In-progress drawing state
  const drawingPolygonRef = useRef<google.maps.Polygon | null>(null);
  const drawingPathRef = useRef<google.maps.LatLngLiteral[]>([]);
  const drawingListenersRef = useRef<google.maps.MapsEventListener[]>([]);

  const { isLoaded, loadError } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: "AIzaSyAumxyJ5Z1j-_X1EHUSy8GCRr21zDPzSHs",
    libraries,
  });

  // Convert a rendered polygon into our GeoJSON boundary shape (closed ring).
  const polygonToBoundary = (
    polygon: google.maps.Polygon
  ): CampusBoundaryData => {
    const path = polygon.getPath();
    const coords: number[][] = [];
    for (let i = 0; i < path.getLength(); i++) {
      const latLng = path.getAt(i);
      coords.push([latLng.lng(), latLng.lat()]);
    }
    if (path.getLength() > 0) {
      const firstPoint = path.getAt(0);
      coords.push([firstPoint.lng(), firstPoint.lat()]);
    }
    return {
      geometry: {
        type: "Polygon",
        coordinates: [coords],
      },
    };
  };

  const commitBoundary = useCallback(
    (boundary: CampusBoundaryData | null, isValid: boolean) => {
      setBoundaryData(boundary);
      if (onBoundaryChange) onBoundaryChange(boundary);
      if (onValidityChange) onValidityChange(isValid);
    },
    [onBoundaryChange, onValidityChange]
  );

  // Make a finished polygon editable, wire edit listeners, and record it.
  const finalizePolygon = useCallback(
    (polygon: google.maps.Polygon) => {
      if (polygonRef.current) {
        polygonRef.current.setMap(null);
      }
      polygonRef.current = polygon;
      polygon.setEditable(true);

      setHasBeenCleared(false);
      commitBoundary(polygonToBoundary(polygon), true);

      const updateFromEdit = () =>
        commitBoundary(polygonToBoundary(polygon), true);

      const path = polygon.getPath();
      ["set_at", "insert_at", "remove_at"].forEach((event) =>
        window.google.maps.event.addListener(path, event, updateFromEdit)
      );
    },
    [commitBoundary]
  );

  const onMapLoad = useCallback(
    (map: google.maps.Map) => {
      mapRef.current = map;

      // Render existing boundary data on first load (unless manually cleared)
      if (
        boundaryData &&
        boundaryData.geometry.coordinates.length &&
        !hasBeenCleared
      ) {
        const pathCoords = boundaryData.geometry.coordinates[0].map(
          ([lng, lat]) => ({ lat, lng })
        );

        const polygon = new window.google.maps.Polygon({
          paths: pathCoords,
          ...polygonStyle,
          clickable: !readOnly,
          editable: !readOnly,
          zIndex: 1,
          map,
        });

        polygonRef.current = polygon;

        if (!readOnly) {
          const updateFromEdit = () =>
            commitBoundary(polygonToBoundary(polygon), true);
          ["set_at", "insert_at", "remove_at"].forEach((event) =>
            window.google.maps.event.addListener(
              polygon.getPath(),
              event,
              updateFromEdit
            )
          );
        }

        // Center the map on the polygon for better viewing
        const bounds = new window.google.maps.LatLngBounds();
        pathCoords.forEach((coord) => bounds.extend(coord));

        if (readOnly) {
          map.fitBounds(bounds, {
            top: 100,
            right: 100,
            bottom: 100,
            left: 100,
          });
        } else {
          map.fitBounds(bounds);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [boundaryData, hasBeenCleared, isDark, readOnly, commitBoundary]
  );

  const onSearchBoxLoad = useCallback(
    (searchBox: google.maps.places.SearchBox) => {
      searchBoxRef.current = searchBox;
      searchBox.addListener("places_changed", () => {
        const places = searchBox.getPlaces();
        if (!places || places.length === 0) return;
        const place = places[0];
        if (!place.geometry || !place.geometry.location) return;
        if (mapRef.current) {
          mapRef.current.setCenter(place.geometry.location);
          mapRef.current.setZoom(16);
        }
      });
    },
    []
  );

  const stopDrawing = useCallback(() => {
    drawingListenersRef.current.forEach((listener) => listener.remove());
    drawingListenersRef.current = [];
    if (drawingPolygonRef.current) {
      drawingPolygonRef.current.setMap(null);
      drawingPolygonRef.current = null;
    }
    drawingPathRef.current = [];
    setVertexCount(0);
    setIsDrawing(false);
  }, []);

  const finishDrawing = useCallback(() => {
    // A polygon needs at least 3 vertices
    if (drawingPathRef.current.length < 3) return;

    const path = [...drawingPathRef.current];

    // Tear down the in-progress drawing state/listeners
    drawingListenersRef.current.forEach((listener) => listener.remove());
    drawingListenersRef.current = [];
    if (drawingPolygonRef.current) {
      drawingPolygonRef.current.setMap(null);
      drawingPolygonRef.current = null;
    }
    drawingPathRef.current = [];
    setVertexCount(0);
    setIsDrawing(false);

    if (!mapRef.current) return;

    const polygon = new window.google.maps.Polygon({
      paths: path,
      ...polygonStyle,
      clickable: true,
      editable: true,
      zIndex: 1,
      map: mapRef.current,
    });

    finalizePolygon(polygon);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finalizePolygon]);

  const handleStartDrawing = useCallback(() => {
    if (!isLoaded || !mapRef.current || !window.google) return;

    // Remove any existing committed polygon — we're drawing a fresh one
    if (polygonRef.current) {
      polygonRef.current.setMap(null);
      polygonRef.current = null;
    }

    // Reset/seed the in-progress drawing
    drawingPathRef.current = [];
    setVertexCount(0);
    if (drawingPolygonRef.current) {
      drawingPolygonRef.current.setMap(null);
    }
    drawingPolygonRef.current = new window.google.maps.Polygon({
      paths: [],
      ...polygonStyle,
      clickable: false,
      editable: false,
      zIndex: 1,
      map: mapRef.current,
    });

    setIsDrawing(true);

    const map = mapRef.current;
    const clickListener = map.addListener(
      "click",
      (e: google.maps.MapMouseEvent) => {
        if (!e.latLng) return;
        drawingPathRef.current.push(e.latLng.toJSON());
        drawingPolygonRef.current?.setPath(drawingPathRef.current);
        setVertexCount(drawingPathRef.current.length);
      }
    );
    // Double-click closes the shape (default dbl-click zoom is disabled below)
    const dblClickListener = map.addListener("dblclick", () => finishDrawing());

    drawingListenersRef.current = [clickListener, dblClickListener];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, finishDrawing]);

  const handleClearBoundary = () => {
    // Cancel any in-progress drawing first
    if (isDrawing) stopDrawing();

    if (polygonRef.current) {
      polygonRef.current.setMap(null);
      polygonRef.current = null;
    }

    setHasBeenCleared(true);
    commitBoundary(null, false);
  };

  // Disable default dbl-click zoom while drawing so dbl-click can close the shape
  useEffect(() => {
    if (!mapRef.current) return;
    mapRef.current.setOptions({ disableDoubleClickZoom: isDrawing });
  }, [isDrawing]);

  // Clean up drawing listeners/overlays on unmount
  useEffect(() => {
    return () => {
      drawingListenersRef.current.forEach((listener) => listener.remove());
      if (drawingPolygonRef.current) {
        drawingPolygonRef.current.setMap(null);
      }
    };
  }, []);

  if (loadError) {
    return (
      <div className="p-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-muted-foreground">
                Error loading Google Maps: {loadError.message}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="mb-0">
      <Card>
        <CardHeader>
          <CardTitle>Campus Boundary</CardTitle>
        </CardHeader>
        <CardContent>
          {!readOnly && (
            <p className="text-muted-foreground mb-4">
              {isDrawing
                ? "Click on the map to add points. Add at least 3, then click Finish (or double-click) to close the boundary."
                : "Draw a polygon around your campus on the map below. Use the search to find your location, then click Draw Boundary and click points on the map."}
            </p>
          )}

          <div className="mb-5 rounded-lg overflow-hidden shadow-lg">
            {!isLoaded ? (
              <div
                style={{
                  ...mapContainerStyle,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                className={`${isDark ? "bg-slate-800" : "bg-gray-100"}`}
              >
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : (
              <>
                {!readOnly && (
                  <div
                    className={`p-3 border-b ${
                      isDark ? "bg-slate-700" : "bg-gray-50"
                    }`}
                  >
                    <div className="flex gap-2 items-center">
                      <StandaloneSearchBox onLoad={onSearchBoxLoad}>
                        <input
                          type="text"
                          placeholder="Search for your campus location"
                          className="px-3 py-2 border rounded-md w-80 bg-background text-foreground"
                        />
                      </StandaloneSearchBox>
                      {isDrawing ? (
                        <>
                          <Button
                            type="button"
                            onClick={finishDrawing}
                            disabled={vertexCount < 3}
                          >
                            Finish{vertexCount > 0 ? ` (${vertexCount})` : ""}
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            onClick={stopDrawing}
                          >
                            Cancel
                          </Button>
                        </>
                      ) : (
                        <Button type="button" onClick={handleStartDrawing}>
                          Draw Boundary
                        </Button>
                      )}
                    </div>
                  </div>
                )}
                <GoogleMap
                  mapContainerStyle={mapContainerStyle}
                  center={center}
                  zoom={13}
                  options={{
                    mapTypeControl: true,
                    streetViewControl: false,
                    fullscreenControl: true,
                    styles: isDark
                      ? [
                          {
                            elementType: "geometry",
                            stylers: [{ color: "#242f3e" }],
                          },
                          {
                            elementType: "labels.text.stroke",
                            stylers: [{ color: "#242f3e" }],
                          },
                          {
                            elementType: "labels.text.fill",
                            stylers: [{ color: "#746855" }],
                          },
                          {
                            featureType: "administrative.locality",
                            elementType: "labels.text.fill",
                            stylers: [{ color: "#d59563" }],
                          },
                          {
                            featureType: "poi",
                            elementType: "labels.text.fill",
                            stylers: [{ color: "#d59563" }],
                          },
                          {
                            featureType: "poi.park",
                            elementType: "geometry",
                            stylers: [{ color: "#263c3f" }],
                          },
                          {
                            featureType: "poi.park",
                            elementType: "labels.text.fill",
                            stylers: [{ color: "#6b9a76" }],
                          },
                          {
                            featureType: "road",
                            elementType: "geometry",
                            stylers: [{ color: "#38414e" }],
                          },
                          {
                            featureType: "road",
                            elementType: "geometry.stroke",
                            stylers: [{ color: "#212a37" }],
                          },
                          {
                            featureType: "road",
                            elementType: "labels.text.fill",
                            stylers: [{ color: "#9ca5b3" }],
                          },
                          {
                            featureType: "road.highway",
                            elementType: "geometry",
                            stylers: [{ color: "#746855" }],
                          },
                          {
                            featureType: "road.highway",
                            elementType: "geometry.stroke",
                            stylers: [{ color: "#1f2835" }],
                          },
                          {
                            featureType: "road.highway",
                            elementType: "labels.text.fill",
                            stylers: [{ color: "#f3d19c" }],
                          },
                          {
                            featureType: "transit",
                            elementType: "geometry",
                            stylers: [{ color: "#2f3948" }],
                          },
                          {
                            featureType: "transit.station",
                            elementType: "labels.text.fill",
                            stylers: [{ color: "#d59563" }],
                          },
                          {
                            featureType: "water",
                            elementType: "geometry",
                            stylers: [{ color: "#17263c" }],
                          },
                          {
                            featureType: "water",
                            elementType: "labels.text.fill",
                            stylers: [{ color: "#515c6d" }],
                          },
                          {
                            featureType: "water",
                            elementType: "labels.text.stroke",
                            stylers: [{ color: "#17263c" }],
                          },
                        ]
                      : [],
                  }}
                  onLoad={onMapLoad}
                />
              </>
            )}
          </div>

          {!readOnly && (
            <div className="flex justify-end mt-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleClearBoundary}
              >
                Clear Boundary
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CampusBoundary;
